import React from "react";
import { Group, Toolbar } from "react-aria-components";

import { Context } from "@reactivated";

import { ScrollNavMenuBlock as BaseMenuBlock } from "@thelabnyc/thelabui/src/components/ScrollNavMenuBlock";
import { type ScrollNavItem } from "@thelabnyc/thelabui/src/components/ScrollNavMenuBlock/hooks";

import { Clickable } from "../Clickables";
import { Separator } from "../Separator";
import { Share } from "../Share";

import styles from "./ScrollNav.module.scss";

export { ScrollNavMenuItemBlock } from "@thelabnyc/thelabui/src/components/ScrollNavMenuBlock";

export const ScrollNavMenu = (props: React.PropsWithChildren) => {
    const { request } = React.useContext(Context);
    const ref = React.useRef<HTMLDivElement>(null);
    const [isOverflowingLeft, setIsOverflowingLeft] = React.useState(false);
    const [isOverflowingRight, setIsOverflowingRight] = React.useState(false);

    const checkOverflow = React.useCallback(() => {
        const element = ref.current;
        if (!element) return;

        const { scrollLeft, scrollWidth, clientWidth } = element;

        // Can scroll left if we're not at the start
        setIsOverflowingLeft(scrollLeft > 0);

        // Can scroll right if we're not at the end (with 1px tolerance for rounding)
        setIsOverflowingRight(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);

    // Check overflow on mount and when children change
    React.useEffect(() => {
        checkOverflow();
    }, [checkOverflow, props.children]);

    // Check overflow on resize
    React.useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [checkOverflow]);

    return (
        <div className={styles.wrapper}>
            <Toolbar
                className={styles.menu}
                data-overflow-left={isOverflowingLeft}
                data-overflow-right={isOverflowingRight}
            >
                <div
                    onScroll={checkOverflow}
                    ref={ref}
                    className={styles.scrollContainer}
                >
                    <Group
                        className={styles.menuGroup}
                        aria-label="Navigate within the page"
                    >
                        {props.children}
                    </Group>
                    <Separator orientation="vertical" />
                    <Share url={request.url} offset={28} />
                </div>
            </Toolbar>
        </div>
    );
};

export const ScrollNavMenuItem = (
    props: React.PropsWithChildren<{ navItem: ScrollNavItem }>,
) => (
    <Clickable href={"#" + props.navItem.elemID} className={styles.menuItem}>
        {props.navItem.name}
    </Clickable>
);

export const ScrollNavMenuBlock = (
    props: React.ComponentProps<typeof BaseMenuBlock>,
) => (
    <BaseMenuBlock
        value={props.value}
        dependencies={{
            ScrollNavMenuContainer: ScrollNavMenu,
            MenuItem: ScrollNavMenuItem,
        }}
    />
);
